import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { euro, dateIt } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Landmark, Building2, Mail, Phone, MapPin } from "lucide-react";

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

function Kpi({ label, value, tid, tone = "text-slate-900" }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3" data-testid={tid}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`font-mono font-semibold text-lg mt-0.5 ${tone}`}>{value}</div>
    </div>
  );
}

export function StoricoAnagrafica({ kind, record, open, onOpenChange }) {
  const isCliente = kind === "clienti";
  const [anno, setAnno] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["storico", kind, record?.id, anno],
    queryFn: async () => (await api.get(`/${kind}/${record.id}/storico`, { params: anno ? { anno } : {} })).data,
    enabled: !!record?.id && open,
  });
  const lbl = isCliente ? { tot: "emesso", pag: "incassato", res: "da_incassare", totL: "Fatturato", pagL: "Incassato", resL: "Da incassare" }
                        : { tot: "acquisti", pag: "pagato", res: "da_pagare", totL: "Acquisti", pagL: "Pagato", resL: "Da pagare" };
  const annoSel = anno ?? data?.anno;
  const meseMap = Object.fromEntries((data?.per_mese || []).map((m) => [m.mese, m]));
  const maxMese = Math.max(1, ...(data?.per_mese || []).map((m) => Math.max(m[lbl.tot], m[lbl.pag])));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="storico-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" /> {record?.ragione_sociale}
            <span className="font-mono text-xs text-slate-400 ml-1">{record?.codice}</span>
          </DialogTitle>
          <DialogDescription>Scheda {isCliente ? "cliente" : "fornitore"} e storico {isCliente ? "fatturato / incassato" : "acquisti / pagamenti"}.</DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-700 bg-orange-50/50 border border-orange-100 rounded-lg p-4" data-testid="storico-anagrafica">
          <div><span className="text-slate-400">P. IVA:</span> {record?.piva || "—"} <span className="text-slate-400 ml-3">CF:</span> {record?.codice_fiscale || "—"}</div>
          <div className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-slate-400" />{[record?.indirizzo, record?.cap, record?.citta, record?.provincia].filter(Boolean).join(", ") || "—"}</div>
          <div className="flex items-center gap-1"><Mail className="h-3.5 w-3.5 text-slate-400" />{record?.email || "—"} {record?.pec && <span className="text-slate-400 ml-2">PEC: {record.pec}</span>}</div>
          <div className="flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-slate-400" />{record?.telefono || "—"} {record?.referente && <span className="text-slate-400 ml-2">Ref. {record.referente}</span>}</div>
          <div><span className="text-slate-400">SDI:</span> {record?.codice_sdi || "—"}</div>
          <div className="flex items-center gap-1"><Landmark className="h-3.5 w-3.5 text-slate-400" /><span className="font-mono text-xs">{record?.iban || "—"}</span></div>
        </div>

        {isLoading || !data ? (
          <div className="py-10 text-center text-slate-400">Caricamento…</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Kpi label={`${lbl.totL} totale`} value={euro(isCliente ? data.totale_emesso : data.totale_acquisti)} tid="storico-kpi-totale" />
              <Kpi label={`${lbl.pagL} totale`} value={euro(isCliente ? data.totale_incassato : data.totale_pagato)} tone="text-emerald-700" tid="storico-kpi-pagato" />
              <Kpi label={lbl.resL} value={euro((isCliente ? data.totale_emesso - data.totale_incassato : data.totale_acquisti - data.totale_pagato))} tone="text-primary" tid="storico-kpi-residuo" />
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Per anno</div>
              {data.per_anno.length === 0 ? (
                <p className="text-sm text-slate-400" data-testid="storico-empty">Nessuna fattura registrata.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100"><th className="py-1.5">Anno</th><th className="py-1.5 text-right">N.</th><th className="py-1.5 text-right">{lbl.totL}</th><th className="py-1.5 text-right">{lbl.pagL}</th><th className="py-1.5 text-right">{lbl.resL}</th></tr></thead>
                  <tbody>
                    {data.per_anno.map((a) => (
                      <tr key={a.anno} className={`border-b border-slate-50 cursor-pointer hover:bg-orange-50/40 ${annoSel === a.anno ? "bg-orange-50/60" : ""}`} onClick={() => setAnno(a.anno)} data-testid={`storico-anno-${a.anno}`}>
                        <td className="py-1.5 font-semibold">{a.anno}</td><td className="py-1.5 text-right font-mono">{a.num}</td>
                        <td className="py-1.5 text-right font-mono">{euro(a[lbl.tot])}</td><td className="py-1.5 text-right font-mono text-emerald-700">{euro(a[lbl.pag])}</td><td className="py-1.5 text-right font-mono text-primary">{euro(a[lbl.res])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {data.anni.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Per mese</div>
                  <Select value={String(annoSel)} onValueChange={(v) => setAnno(Number(v))}>
                    <SelectTrigger className="w-28 h-8" data-testid="storico-anno-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{data.anni.map((a) => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-12 gap-1.5 items-end h-28" data-testid="storico-mesi-chart">
                  {MESI.map((m, i) => {
                    const key = `${annoSel}-${String(i + 1).padStart(2, "0")}`;
                    const v = meseMap[key];
                    const t = v?.[lbl.tot] || 0, p = v?.[lbl.pag] || 0;
                    return (
                      <div key={m} className="flex flex-col items-center justify-end h-full" title={`${m}: ${lbl.totL} ${euro(t)} · ${lbl.pagL} ${euro(p)}`}>
                        <div className="w-full flex items-end gap-0.5 flex-1">
                          <div className="flex-1 bg-primary/80 rounded-t" style={{ height: `${(t / maxMese) * 100}%` }} />
                          <div className="flex-1 bg-emerald-500/80 rounded-t" style={{ height: `${(p / maxMese) * 100}%` }} />
                        </div>
                        <span className="text-[10px] text-slate-400 mt-1">{m}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-4 text-[11px] text-slate-500 mt-1"><span><span className="inline-block w-2.5 h-2.5 bg-primary/80 rounded-sm mr-1" />{lbl.totL}</span><span><span className="inline-block w-2.5 h-2.5 bg-emerald-500/80 rounded-sm mr-1" />{lbl.pagL}</span></div>

                <table className="w-full text-sm mt-3" data-testid="storico-fatture-table">
                  <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100"><th className="py-1.5">Data</th><th className="py-1.5">N.</th><th className="py-1.5">Commessa</th><th className="py-1.5">Descrizione</th><th className="py-1.5 text-right">Importo</th><th className="py-1.5 text-right">{isCliente ? "Incassata" : "Pagata"}</th></tr></thead>
                  <tbody>
                    {data.fatture.map((f) => (
                      <tr key={f.id} className="border-b border-slate-50">
                        <td className="py-1.5">{dateIt(f.data)}</td><td className="py-1.5 font-mono text-xs">{isCliente ? f.numero : f.numero_fattura || "—"}</td>
                        <td className="py-1.5 font-mono text-xs text-slate-500">{f.commessa_codice}</td><td className="py-1.5 text-slate-600 truncate max-w-[200px]">{isCliente ? f.note : f.descrizione}</td>
                        <td className="py-1.5 text-right font-mono">{euro(isCliente ? f.importo : f.costo_totale)}</td>
                        <td className="py-1.5 text-right text-xs">{(isCliente ? f.data_incasso : f.data_pagamento) ? <span className="text-emerald-700">{dateIt(isCliente ? f.data_incasso : f.data_pagamento)}</span> : <span className="text-primary font-medium">{isCliente ? "Da incassare" : "Da pagare"}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
